import mixpanel from 'mixpanel-browser'
import { redactPublicReportToken } from '@/utils/publicReportRoute'

/**
 * Event name constants — use these instead of raw strings to prevent typos
 * and enable autocomplete.
 */
export const ANALYTICS_EVENTS = {
  // Page views (auto-tracked via router)
  PAGE_VIEWED: 'Page Viewed',

  // Auth
  LOGIN_SUCCESS: 'Login Success',
  LOGIN_FAILED: 'Login Failed',
  LOGOUT: 'Logout',

  // Content
  EXPERIMENT_CREATED: 'Experiment Created',
  EXPERIMENT_VIEWED: 'Experiment Viewed',
  FILE_UPLOADED: 'File Uploaded',
  FOLDER_CREATED: 'Folder Created',

  // Features
  PDF_EXPORTED: 'PDF Exported',
  THEME_CHANGED: 'Theme Changed',
  INSIGHT_GENERATED: 'Insight Generated',
  NOTIFICATIONS_MARKED_ALL_READ: 'Marked All Notifications As Read',

  // Upload telemetry (SYN-559) — distribution / funnel; pair with
  // Sentry captureException for the alerting side.
  UPLOAD_STARTED: 'Upload Started',
  UPLOAD_COMPLETED: 'Upload Completed',
  UPLOAD_FAILED: 'Upload Failed',

  // Analysis completion telemetry (SYN-435) — mede a taxa do "frontend recebe"
  // pareada com o "backend emite" do lado de lá, e quanto a reconciliação por
  // processingQueue está cobrindo um evento WS perdido.
  WS_STATUS_UPDATE_RECEIVED: 'WS Status Update Received',
  STUCK_RECONCILE_TRIGGERED: 'Stuck Reconcile Triggered',
}

let initialized = false

function isDevMode() {
  const hostname = window.location.hostname
  return hostname === 'localhost' || hostname === '127.0.0.1'
}

/**
 * Normaliza o valor bruto de VITE_MIXPANEL_REPLAY_PERCENT (string, número ou
 * undefined) para um inteiro 0–100. Ausente/inválido → 0 (Replay desligado).
 * Parse fora do main.js de propósito: main.js está fora da cobertura.
 */
function parseReplayPercent(raw) {
  // Number() em vez de parseInt(): '1e2' vira 100 (não 1) e um typo tipo '50%'
  // ou '12px' vira 0 em vez de ligar o Replay em silêncio. Só inteiro finito passa.
  if (raw === '' || raw === null || raw === undefined || typeof raw === 'boolean') return 0
  const parsed = Number(raw)
  if (!Number.isInteger(parsed) || parsed < 0) return 0
  if (parsed > 100) return 100
  return parsed
}

/**
 * Remove query string e fragmento de uma URL, preservando o path.
 * pt-BR: o SDK carimba `$current_url = window.location.href` em TODO evento
 * (`mixpanel-browser/src/utils.js`, `_.info.currentUrl`), então rotas que
 * recebem segredo por query (`/first-access?token=…`, `/reset-password?token=…`,
 * retorno do SSO) vazariam a credencial em cada clique/tecla/scroll do
 * autocapture. R1 aceita ID em path, nunca query.
 * en: strip query/fragment, keep the path.
 */
function stripQuery(url) {
  return url.split(/[?#]/)[0]
}

// SYN-2721: em `/r/:token` a credencial está no PATH, e tirar a query não basta.
function stripCredentials(url) {
  return redactPublicReportToken(stripQuery(url))
}

/** Propriedades de referrer que podem carregar a query de uma rota com token. */
const REFERRER_PROPS = ['$referrer', '$initial_referrer']

function sanitizeEvent(event) {
  const properties = event && event.properties
  if (!properties) return event

  const sanitized = { ...properties }
  if (typeof sanitized.$current_url === 'string') {
    sanitized.$current_url = stripCredentials(sanitized.$current_url)
  }
  // Referrer só é tocado quando carrega credencial: fora disso a query é sinal
  // de aquisição (utm_*) que o produto usa no painel.
  REFERRER_PROPS.forEach((key) => {
    const value = sanitized[key]
    if (typeof value === 'string' && value.includes('token=')) {
      sanitized[key] = stripQuery(value)
    }
  })
  return { ...event, properties: sanitized }
}

/**
 * Hook `hooks.before_send_events` do `mixpanel.init`.
 *
 * Contrato real (mixpanel-browser 2.82.0): `_run_hook('before_send_events', …)`
 * (`mixpanel-core.js`) passa UM payload `{ event, properties }` — tanto no
 * caminho imediato (`_track_or_batch`) quanto no `beforeSendHook` do
 * `RequestBatcher` — e usa o valor devolvido; `null` cancela o envio e
 * `undefined` faz o SDK reportar erro e descartar o evento. Devolver array é
 * armadilha: `_run_hook` dá `splice` dos argumentos com os elementos e só o
 * primeiro sobrevive. Por isso: array na entrada → array na saída, objeto →
 * objeto, e nunca cancelamos evento (só limpamos URL).
 */
export function sanitizeEvents(payload) {
  if (Array.isArray(payload)) return payload.map(sanitizeEvent)
  return sanitizeEvent(payload)
}

const analytics = {
  init(token, { replayPercent } = {}) {
    if (!token) {
      console.warn('[Analytics] VITE_MIXPANEL_TOKEN not set — Mixpanel disabled.')
      return
    }

    if (isDevMode()) {
      console.log('[Analytics] Dev mode — events will be logged to console only.')
      initialized = true
      return
    }

    try {
      mixpanel.init(token, {
        debug: false,
        track_pageview: false,
        persistence: 'localStorage',
        autocapture: {
          pageview: false, // R2 — o Page Viewed custom continua sendo a fonte
          click: true,
          input: true,
          submit: true,
          scroll: true,
          rage_click: true,
          dead_click: true,
          capture_text_content: true, // R1 é garantida pelos wrappers mp-sensitive
        },
        record_sessions_percent: parseReplayPercent(replayPercent),
        // Última linha de defesa: tira a query de `$current_url` em todo evento.
        hooks: { before_send_events: sanitizeEvents },
      })
      initialized = true
    } catch (error) {
      console.error('[Analytics] Failed to initialize Mixpanel:', error)
    }
  },

  track(eventName, properties = {}) {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Track:', eventName, properties)
      return
    }

    try {
      mixpanel.track(eventName, properties)
    } catch (error) {
      console.error('[Analytics] Track error:', error)
    }
  },

  identify(userId) {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Identify:', userId)
      return
    }

    try {
      mixpanel.identify(userId)
    } catch (error) {
      console.error('[Analytics] Identify error:', error)
    }
  },

  setUserProfile(properties) {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Set User Profile:', properties)
      return
    }

    try {
      mixpanel.people.set(properties)
    } catch (error) {
      console.error('[Analytics] Set profile error:', error)
    }
  },

  registerSuperProperties(props) {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Register Super Properties:', props)
      return
    }

    try {
      mixpanel.register(props)
    } catch (error) {
      console.error('[Analytics] Register super properties error:', error)
    }
  },

  unregisterSuperProperty(name) {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Unregister Super Property:', name)
      return
    }

    try {
      mixpanel.unregister(name)
    } catch (error) {
      console.error('[Analytics] Unregister super property error:', error)
    }
  },

  reset() {
    if (!initialized) return

    if (isDevMode()) {
      console.log('[Analytics] Reset (user logged out)')
      return
    }

    try {
      mixpanel.reset()
    } catch (error) {
      console.error('[Analytics] Reset error:', error)
    }
  },

  trackPageView(routeName, routePath) {
    this.track(ANALYTICS_EVENTS.PAGE_VIEWED, {
      page_name: routeName,
      page_path: routePath,
    })
  },
}

export default analytics
